<template>
  <section class="sb-empty-state" :class="computedClass">
    <div
      v-if="hasIconOrImage"
      class="sb-empty-state__icon"
      :class="{ 'sb-empty-state__icon--rounded': iconRounded }"
    >
      <template v-if="showIcon">
        <SbIconCircle
          v-if="iconRounded"
          :variant="iconRounded ? 'success' : undefined"
        >
          <SbIcon :name="computedIconName" size="large" />
        </SbIconCircle>
        <SbIcon v-else :name="computedIconName" />
      </template>

      <SbIllustration
        v-else-if="illustrationLink.length"
        :src="illustrationLink"
        :width="illustrationWidth"
      />
      <div v-if="topRightIcon" class="sb-empty-state__icon-small">
        <SbIcon :name="topRightIcon" />
      </div>
    </div>
    <h3 class="sb-empty-state__title">{{ title }}</h3>
    <p
      v-if="$slots.description"
      :class="[
        'sb-empty-state__text',
        { 'sb-empty-state__text-wide': wideDescription },
      ]"
    >
      <slot name="description" />
    </p>
    <div class="sb-empty-state__buttons">
      <SbLink
        v-if="secondButtonText"
        :href="secondButtonHref"
        rel="noopener noreferrer"
        target="_blank"
      >
        <SbButton
          tabindex="-1"
          variant="secondary"
          :size="secondButtonSize"
          :label="secondButtonText"
          :is-disabled="isLoading"
          data-testid="empty-state-secondary-button"
          @click="emit('secondary-button-click')"
        />
      </SbLink>
      <SbButton
        v-if="buttonText"
        v-tooltip="{
          position: 'bottom',
          label: buttonTooltip,
        }"
        :size="buttonSize"
        :icon="buttonIcon"
        :label="buttonText"
        :is-disabled="isButtonDisabled"
        :is-loading="isLoading"
        data-testid="empty-state-primary-button"
        @click="emit('button-click')"
      />
    </div>
    <SbLink
      v-if="linkText && href"
      :href="href"
      class="sb-empty-state__link"
      :label="linkText"
      :icon-right="linkIcon"
      target="_blank"
      rel="noopener noreferrer"
    />
    <router-link
      v-else-if="linkText && !href && routeTo"
      :to="routeTo"
      class="sb-empty-state__router"
    >
      {{ linkText }}
    </router-link>
    <SbButton
      v-else-if="linkText && !href && !routeTo"
      :icon="linkIcon"
      :label="linkText"
      :is-loading="isLoading"
      variant="quaternary"
      is-clear
      data-testid="empty-state-link"
      @click="emit('link-click')"
    />
    <SbLoading v-if="isLoading" color="primary" />
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import SbIcon from '../Icon'
import SbLink from '../Link'
import SbButton from '../Button'
import SbLoading from '../Loading'
import SbIllustration from '../Illustration'
import SbIconCircle from '../IconCircle'

export type SbEmptyStateProps = {
  title?: string
  noImage?: boolean
  illustrationLink?: string
  illustrationWidth?: string
  iconName?: string
  iconRounded?: boolean
  topRightIcon?: string
  buttonText?: string
  secondButtonText?: string
  secondButtonHref?: string
  linkText?: string
  linkIcon?: string
  href?: string
  routeTo?: string | Record<string, unknown>
  secondButtonSize?: 'small' | 'normal'
  buttonIcon?: string
  buttonSize?: 'small' | 'normal'
  isButtonDisabled?: boolean
  buttonTooltip?: string
  small?: boolean
  wideDescription?: boolean
  isSearch?: boolean
  isLoading?: boolean
  fullHeight?: boolean
}

const props = withDefaults(defineProps<SbEmptyStateProps>(), {
  illustrationLink:
    'https://a.storyblok.com/f/95975/x/f6ea3e1d2c/workspace-empty.svg',
  illustrationWidth: '120px',
  secondButtonSize: 'small',
  buttonIcon: 'plus',
  buttonSize: 'small',
})

const emit = defineEmits<{
  (e: 'button-click'): void
  (e: 'secondary-button-click'): void
  (e: 'link-click'): void
}>()

const computedClass = computed(() => {
  return {
    'sb-empty-state--small': props.small,
    'sb-empty-state--full': props.fullHeight,
  }
})

const hasIconOrImage = computed(() => {
  return (
    (!props.noImage && props.illustrationLink?.length) || props.iconName?.length
  )
})

const computedIconName = computed(() =>
  props.iconName?.length ? props.iconName : 'empty-search-pictogram',
)

const showIcon = computed(() => props.iconName?.length || props.isSearch)
</script>
